import {
  AlertCircle,
  ArrowLeft,
  Calendar,
  CheckCircle,
  Clock,
  Download,
  Edit2,
  MoreVertical,
  Trash2,
  Watch,
  XCircle,
} from 'lucide-react'
import type React from 'react'
import { useEffect, useRef, useState } from 'react'
import Skeleton from 'react-loading-skeleton'
import { useNavigate } from 'react-router-dom'
import 'react-loading-skeleton/dist/skeleton.css'
import styled from 'styled-components'
import { supabase } from '../../lib/supabase'
import { useAthleteThresholds } from '../../hooks/useAthleteThresholds'
import { FeatureFlagKey } from '../../hooks/useFeatureFlags'
import { useHeartRateStats } from '../../hooks/useHeartRateStats'
import { usePowerStats } from '../../hooks/usePowerStats'
import { useWorkoutAnalysis } from '../../hooks/useWorkoutAnalysis'
import { useWorkoutLengths } from '../../hooks/useWorkoutLengths'
import { useWorkoutSessions } from '../../hooks/useWorkoutSessions'
import { useWorkoutSteps, useWorkouts } from '../../hooks/useWorkouts'
import { theme } from '../../theme/theme'
import type { WorkoutLap } from '../../types/database.tables'
import type { WorkoutWithData } from '../../types/workout'
import type { CalculatedZone } from '../../types/zones'
import { formatLongDate, formatTime } from '../../utils/dateUtils'
import { formatDeviceAttribution } from '../../utils/deviceUtils'
import { getDisciplineColor } from '../../utils/disciplineColors'
import { getEntryIcon } from '../../utils/disciplineIcons'
import { CriticalPowerChart } from '../charts/CriticalPowerChart'
import { HeartRateHistogram } from '../charts/HeartRateHistogram'
import { LapsChart } from '../charts/LapsChart'
import { MetricsChart } from '../charts/MetricsChart'
import { PowerZonesChart } from '../charts/PowerZonesChart'
import { SplitsChart } from '../charts/SplitsChart'
import { WBalChart } from '../charts/WBalChart'
import { ActionButton } from '../common/ActionButton'
import { Button } from '../common/Button'
import { FeatureFlag } from '../common/FeatureFlag'
import { WorkoutKeyMetrics } from '../workout/WorkoutKeyMetrics'

// Use LapDisplayData from types instead of local interface

const Container = styled.div`
  height: 100%;
  background: ${({ theme }) => theme.colors.background};
  padding: 0 ${({ theme }) => theme.spacing.md};
  overflow-y: auto;

  @media (max-width: 640px) {
    padding: 0;
  }
`

const Header = styled.div<{ $scrolled: boolean }>`
  display: flex;
  align-items: center;
  background: ${({ theme }) => theme.colors.background};
  gap: ${({ theme }) => theme.spacing.md};
  padding-bottom: ${({ $scrolled }) => ($scrolled ? theme.spacing.xs : theme.spacing.sm)};
  padding-top: ${({ $scrolled }) => ($scrolled ? theme.spacing.xs : '0')};
  position: fixed;
  width: calc(100%);
  z-index: 100;
  transition: all 0.3s ease;
  border-bottom: ${({ $scrolled, theme }) => ($scrolled ? `1px solid ${theme.colors.border}` : 'none')};
  box-shadow: ${({ $scrolled, theme }) => ($scrolled ? theme.shadows.sm : 'none')};
  margin-left: -16px;
  padding-left: 8px;
  margin-top: 1px;

  @media (max-width: 640px) {
    gap: ${({ theme }) => theme.spacing.md};
    padding-bottom: ${({ $scrolled }) => ($scrolled ? theme.spacing.xs : theme.spacing.md)};
    width: 100%;
    margin-left: 0;
  }
`

const HeaderContent = styled.div`
  flex: 1;
  min-width: 0;
`

const WorkoutTitle = styled.h1<{ $scrolled: boolean }>`
  font-size: ${({ $scrolled }) => ($scrolled ? theme.typography.lg : theme.typography.xxl)};
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text};
  margin: 0 0 ${({ $scrolled }) => ($scrolled ? '0' : theme.spacing.sm)} 0;
  display: flex;
  align-items: center;
  gap: ${({ $scrolled }) => ($scrolled ? theme.spacing.sm : theme.spacing.md)};
  transition: all 0.3s ease;

  svg {
    width: ${({ $scrolled }) => ($scrolled ? '18px' : '24px')};
    height: ${({ $scrolled }) => ($scrolled ? '18px' : '24px')};
    transition: all 0.3s ease;
  }

  @media (max-width: 640px) {
    font-size: ${({ $scrolled }) => ($scrolled ? theme.typography.md : theme.typography.xl)};
    gap: ${({ theme }) => theme.spacing.sm};
    margin: 0 0 ${({ theme, $scrolled }) => ($scrolled ? 0 : theme.spacing.xs)} 0;
  }
`

const EntryIcon = styled.div<{ $disciplineColor?: string }>`
  display: inline-block;
  color: ${(props) => props.$disciplineColor || props.theme.colors.textSecondary};
`

const WorkoutMeta = styled.div<{ $scrolled: boolean }>`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.lg};
  font-size: ${theme.typography.md};
  color: ${({ theme }) => theme.colors.textSecondary};
  flex-wrap: wrap;
  max-height: ${({ $scrolled }) => ($scrolled ? '0' : '100px')};
  opacity: ${({ $scrolled }) => ($scrolled ? 0 : 1)};
  overflow: hidden;
  transition: all 0.3s ease;

  @media (max-width: 640px) {
    gap: ${theme.spacing.sm};
    font-size: ${theme.typography.sm};
  }
`

const MetaItem = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  white-space: nowrap;

  @media (max-width: 640px) {
    gap: ${theme.spacing.xs};
  }
`

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${theme.spacing.lg};
  margin-bottom: ${theme.spacing.md};
  margin-top: 110px;

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, calc(50% - 12px));
    margin-top: 160px;
  }
`

const ContentGrid2 = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${theme.spacing.lg};
  margin-bottom: ${theme.spacing.md};
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(2, calc(50% - 12px));
  }
`

const Card = styled.div`
  background: ${({ theme }) => theme.colors.cardBackground};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing.md};
  position: relative;

  @media (max-width: 640px) {
    padding: ${theme.spacing.sm};
  }
`

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: ${theme.spacing.sm};
`

const CardHeaderContent = styled.div`
  flex: 1;
`

const CardHeaderActions = styled.div`
  display: flex;
  gap: ${theme.spacing.xs};
  opacity: 0;
  transition: opacity 0.2s ease-in-out;

  ${Card}:hover & {
    opacity: 1;
  }
`

const SectionTitle = styled.h2`
  font-size: ${theme.typography.lg};
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text};
  margin: 0 0 ${theme.spacing.xs} 0;
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
`

const CardSubTitle = styled.p`
  font-size: ${theme.typography.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
  margin: 0 0 ${theme.spacing.md} 0;
  line-height: 1.5;
`

const FullWidthSection = styled.div`
  grid-column: 1 / -1;
`

// Analysis section components
const AnalysisSection = styled.div`
  background: ${({ theme }) => theme.colors.cardBackground};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing.lg};
  margin-bottom: ${theme.spacing.lg};
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.md};

  @media (max-width: 640px) {
    padding: ${theme.spacing.md};
    margin-bottom: ${theme.spacing.md};
  }
`

const ExecutionGrade = styled.div<{ $grade: string }>`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  border-radius: ${theme.borderRadius.md};
  font-weight: 600;
  font-size: ${theme.typography.md};
  color: ${({ $grade, theme }) => {
    switch ($grade) {
      case 'excellent':
        return '#10b981' // green
      case 'partially':
        return '#f59e0b' // amber
      case 'poor':
        return theme.colors.error
      default:
        return theme.colors.textSecondary
    }
  }};
`

const InsightsList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.sm};
`

const InsightItem = styled.li<{ $level: string }>`
  border-radius: ${theme.borderRadius.sm};
  font-size: ${theme.typography.sm};

  font-weight: ${({ $level }) => {
    switch ($level) {
      case 'significant':
        return 700
      default:
        return 400
    }
  }};
`

const InsightCategory = styled.button`
  padding: ${theme.spacing.xs} ${theme.spacing.xs};
  margin-right: ${theme.spacing.sm};
  font-size: ${theme.typography.xs};
  background-color: ${({ theme }) => theme.colors.surface};
  border: none;
  border-radius: ${theme.borderRadius.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
  cursor: pointer;
`

const ErrorText = styled.div`
  color: ${({ theme }) => theme.colors.error};
  padding: ${theme.spacing.md};
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
`

const SkeletonWrapper = styled.div`
  .react-loading-skeleton {
    --base-color: ${({ theme }) => theme.colors.surface};
    --highlight-color: ${({ theme }) => theme.colors.border};
    border-radius: ${theme.borderRadius.md};
  }
`

const ChartSkeletonContainer = styled.div`
  width: 100%;
  height: 300px;
  display: flex;
  align-items: center;
  justify-content: center;
`

const MenuButton = styled.button`
  background: transparent;
  border: none;
  color: ${({ theme }) => theme.colors.text};
  cursor: pointer;
  padding: ${theme.spacing.xs};
  border-radius: ${theme.borderRadius.sm};
  display: flex;
  align-items: center;
  justify-content: center;
  transition: ${theme.transitions.default};
  position: relative;

  &:hover {
    background: ${({ theme }) => theme.colors.surface};
  }
`

const MenuContainer = styled.div`
  position: relative;
`

const DropdownMenu = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  background: ${({ theme }) => theme.colors.cardBackground};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${theme.borderRadius.md};
  box-shadow: ${({ theme }) => theme.shadows.lg};
  min-width: 180px;
  z-index: 1000;
  overflow: hidden;
`

const MenuItem = styled.button<{ $destructive?: boolean }>`
  width: 100%;
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  background: transparent;
  border: none;
  color: ${({ theme, $destructive }) => ($destructive ? theme.colors.error : theme.colors.text)};
  text-align: left;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  font-size: ${theme.typography.sm};
  transition: ${theme.transitions.default};

  &:hover {
    background: ${({ theme }) => theme.colors.surface};
  }

  svg {
    width: 16px;
    height: 16px;
  }
`

const ConfirmDialog = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
`

const ConfirmDialogContent = styled.div`
  background: ${({ theme }) => theme.colors.cardBackground};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing.xl};
  max-width: 400px;
  width: 90%;
  box-shadow: ${({ theme }) => theme.shadows.lg};
`

const ConfirmDialogTitle = styled.h3`
  font-size: ${theme.typography.lg};
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text};
  margin: 0 0 ${theme.spacing.sm} 0;
`

const ConfirmDialogMessage = styled.p`
  font-size: ${theme.typography.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
  margin: 0 0 ${theme.spacing.lg} 0;
  line-height: 1.5;
`

const ConfirmDialogActions = styled.div`
  display: flex;
  gap: ${theme.spacing.sm};
  justify-content: flex-end;
`

const ConfirmButton = styled.button<{ $variant: 'primary' | 'danger' }>`
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  border-radius: ${theme.borderRadius.sm};
  border: none;
  font-size: ${theme.typography.sm};
  font-weight: 500;
  cursor: pointer;
  transition: ${theme.transitions.default};

  ${({ $variant, theme }) =>
    $variant === 'primary'
      ? `
    background: ${theme.colors.surface};
    color: ${theme.colors.text};
    &:hover {
      background: ${theme.colors.border};
    }
  `
      : `
    background: ${theme.colors.error};
    color: white;
    &:hover {
      opacity: 0.9;
    }
  `}
`

interface CalculatedMetrics {
  movingTimeSec: number
  restTimeSec: number
}

interface WorkoutAnalysisViewProps {
  workout: WorkoutWithData
  onBack: () => void
}

export const WorkoutAnalysisView: React.FC<WorkoutAnalysisViewProps> = ({ workout, onBack }) => {
  const navigate = useNavigate()
  const { loadWorkoutLaps, deleteWorkout } = useWorkouts()
  const { data: steps } = useWorkoutSteps(workout.id)
  const [laps, setLaps] = useState<WorkoutLap[]>([])
  const [calculatedMetrics, setCalculatedMetrics] = useState<CalculatedMetrics | null>(null)
  const [isScrolled, setIsScrolled] = useState(false)
  const [menuOpen, setMenuOpen] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)

  // Use workout analysis hook
  const { data: analysisResult, isLoading: analysisLoading, error: analysisError } = useWorkoutAnalysis(workout.id)
  const isLoading = analysisLoading

  const EntryTypeIconComponent = getEntryIcon(workout.activity_type ?? 'unknown')
  const isCycling = workout.activity_type?.includes('cycling')
  const isSwimming = workout.activity_type?.includes('swimming')
  const isRunning = workout.activity_type?.includes('running')

  // Get base activity type for threshold link
  const getBaseActivityType = () => {
    const activityType = workout.activity_type?.toLowerCase() || ''
    if (activityType.includes('swim')) return 'swimming'
    if (activityType.includes('cycling') || activityType.includes('bike')) return 'cycling'
    if (activityType.includes('run')) return 'running'
    return null
  }

  const handleEditThresholds = () => {
    const baseActivityType = getBaseActivityType()
    const sportParam = baseActivityType ? `?sport=${baseActivityType}` : ''
    navigate(`/athletes/${workout.user_id}/thresholds${sportParam}`)
  }

  // Metric tooltips
  const metricTooltips = {
    totalEnergy: {
      title: 'Total Energy',
      description:
        'Total calories burned during the workout. Calculated by the recording device based on power, heart rate, and personal data; values may differ slightly between devices.',
    },
    avgPower: {
      title: 'Average Power',
      description:
        'Mean of all 1-second power samples recorded during the workout, including low-power and idle periods. Represents the overall effort across the full session.',
    },
    normalizedPower: {
      title: 'Adjusted Power',
      description:
        'Estimates how hard the activity really felt. Uses a 30-second rolling average and a 4th-power weighting so short, intense efforts count more than easy ones. Reflects the true physiological load more accurately than simple average power.',
    },
    idleTime: {
      title: 'Idle Time',
      description:
        'Time spent stopped or pedaling lightly (< 25 W). Keeping idle time low helps maintain steady oxygen demand and maximize training effectiveness.',
    },
    movingTime: {
      title: 'Moving Time',
      description:
        'Total time spent in motion during the workout, including coasting or low-power periods. Excludes only pauses.',
    },
    restTime: {
      title: 'Rest Time',
      description: 'Time spent inactive between intervals.',
    },
    strokesPerLength: {
      title: 'Average Strokes Per Length',
      description:
        'Efficient freestyle swimmers typically fall between 16–22 strokes per 25 m, while counts above 24 suggest lost propulsion or over-gliding. Counts below 16 indicate increasingly advanced efficiency, with 14–16 being competitive and ≤13 elite.',
    },
  }

  // Fetch workout summaries data (prioritize this over session data)
  const { data: workoutSession } = useWorkoutSessions(workout.id)

  // Fetch heart rate statistics (including standard deviation)
  const { stats: hrStats } = useHeartRateStats(workout.id)
  const { stats: powerStats } = usePowerStats(workout.id)

  // Fetch workout lengths for swimming workouts
  const { loadWorkoutLengths, avgStrokesPerLength: calculatedAvgStrokes } = useWorkoutLengths()

  // Fetch athlete thresholds for the workout date
  const { getThresholdsForDate } = useAthleteThresholds(workout.user_id)
  const [workoutThresholds, setWorkoutThresholds] = useState<{
    vt1?: number
    vt2?: number
    hrZones?: CalculatedZone[]
    paceZones?: CalculatedZone[]
    powerZones?: CalculatedZone[]
    maxHr?: number
  }>({
    vt1: undefined,
    vt2: undefined,
    maxHr: undefined,
    hrZones: undefined,
    paceZones: undefined,
    powerZones: undefined,
  })

  // const _totalDuration = workout.executedTotalDurationSeconds ?? (workout.duration_minutes || 60) * 60

  // Key metrics
  // const _avgPower = workoutSession?.average_power_watts
  // const _normalizedPower = workoutSession?.normalized_power_watts
  // const _totalCalories = workoutSession?.total_calories ?? 0
  // const _totalDistance = workoutSession?.total_distance ?? 0
  // const _avgHeartRate = workoutSession?.average_heart_rate_bpm ?? 0
  const avgStrokesPerLength = calculatedAvgStrokes
  const avgSpeed = workoutSession?.average_speed_mps ? (workoutSession.average_speed_mps * 3600) / 1000 : 0
  // calculate pace min/km from average speed m/s
  const avgPace = avgSpeed > 0 ? 60 / avgSpeed : 0
  // calculate idle time in seconds, where power was below threshold for 10 seconds
  const idleTime: number | undefined = powerStats?.idleTime

  useEffect(() => {
    const loadData = async () => {
      if (workout.id) {
        const laps = await loadWorkoutLaps(workout.id)
        setLaps(laps)

        // Load workout lengths for swimming workouts
        if (
          isSwimming &&
          workout.workout_sessions &&
          workout.workout_sessions.length > 0 &&
          workout.workout_sessions[0].id
        ) {
          await loadWorkoutLengths(workout.workout_sessions[0].id)
        }

        const movingTimeSec = laps
          .filter((lap) => lap.intensity !== 'rest')
          .reduce((acc, lap) => acc + (lap.total_moving_time ?? lap.total_timer_time ?? 0), 0)
        const restTimeSec = laps.reduce((acc, lap, _i) => {
          let restTime = 0
          if (lap.total_strokes === 0) {
            restTime += lap.total_elapsed_time ?? 0
          }
          return acc + restTime
        }, 0)

        setCalculatedMetrics({
          movingTimeSec,
          restTimeSec,
        })
      }
    }
    loadData()
  }, [workout, loadWorkoutLaps, loadWorkoutLengths, isSwimming])

  // Load thresholds for workout date and sport type
  useEffect(() => {
    const loadThresholds = async () => {
      if (workout.scheduled_date && workout.activity_type) {
        // map to base activity type
        let baseActivityType = workout.activity_type.toLowerCase()
        if (baseActivityType.includes('running')) {
          baseActivityType = 'running'
        } else if (baseActivityType.includes('cycling')) {
          baseActivityType = 'cycling'
        } else if (baseActivityType.includes('swimming')) {
          baseActivityType = 'swimming'
        }

        const thresholds = await getThresholdsForDate(baseActivityType, workout.scheduled_date)
        if (thresholds) {
          setWorkoutThresholds({
            vt1: thresholds.vt1Bpm ?? undefined,
            vt2: thresholds.vt2Bpm ?? undefined,
            maxHr: thresholds.maxHrBpm ?? undefined,
            hrZones: thresholds.hrZones?.zones ?? undefined,
            paceZones: thresholds.paceZones?.zones ?? undefined,
            powerZones: thresholds.powerZones?.zones ?? undefined,
          })
        }
      }
    }
    loadThresholds()
  }, [workout.scheduled_date, workout.activity_type, getThresholdsForDate])

  // Handle scroll event to shrink header
  useEffect(() => {
    const handleScroll = () => {
      if (containerRef.current) {
        const scrollTop = containerRef.current.scrollTop
        setIsScrolled(scrollTop > 50)
      }
    }

    const container = containerRef.current
    if (container) {
      container.addEventListener('scroll', handleScroll)
      return () => container.removeEventListener('scroll', handleScroll)
    }
  }, [])

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setMenuOpen(false)
      }
    }

    if (menuOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [menuOpen])

  const handleDeleteClick = () => {
    setMenuOpen(false)
    setShowDeleteConfirm(true)
  }

  const handleConfirmDelete = async () => {
    try {
      await deleteWorkout(workout.id)
      setShowDeleteConfirm(false)
      onBack()
    } catch (error) {
      console.error('Failed to delete workout:', error)
      // TODO: Show error notification to user
    }
  }

  const handleCancelDelete = () => {
    setShowDeleteConfirm(false)
  }

  const handleDownloadFitFile = async () => {
    setMenuOpen(false)

    // Get the file storage path from workout sessions
    const session = workoutSession
    if (!session?.file_storage_path || !session?.file_bucket) {
      console.error('No file storage path found for this workout')
      // TODO: Show error notification to user
      return
    }

    try {
      // Download the file from Supabase storage
      const { data, error } = await supabase.storage.from(session.file_bucket).download(session.file_storage_path)

      if (error) {
        console.error('Error downloading file:', error)
        // TODO: Show error notification to user
        return
      }

      // Create a download link and trigger it
      const url = URL.createObjectURL(data)
      const link = document.createElement('a')
      link.href = url
      link.download = session.file_original_name || `${workout.title}.fit`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)
    } catch (error) {
      console.error('Failed to download FIT file:', error)
      // TODO: Show error notification to user
    }
  }

  return (
    <Container ref={containerRef}>
      <Header $scrolled={isScrolled}>
        <ActionButton onClick={onBack} title="Close">
          <ArrowLeft size={36} />
        </ActionButton>
        <HeaderContent>
          <WorkoutTitle $scrolled={isScrolled}>
            <EntryIcon $disciplineColor={getDisciplineColor(workout.activity_type ?? 'unknown')}>
              <EntryTypeIconComponent size={24} />
            </EntryIcon>
            {workout.title}
          </WorkoutTitle>
          <WorkoutMeta $scrolled={isScrolled}>
            <MetaItem>
              <Calendar size={16} />
              {formatLongDate(workout.scheduled_date)}
            </MetaItem>
            {workout.time && (
              <MetaItem>
                <Clock size={16} />
                {formatTime(workout.time)}
              </MetaItem>
            )}
            {formatDeviceAttribution(workoutSession?.device_model, workoutSession?.source) && (
              <MetaItem>
                <Watch size={16} />
                {formatDeviceAttribution(workoutSession?.device_model, workoutSession?.source)}
              </MetaItem>
            )}
          </WorkoutMeta>
        </HeaderContent>
        <MenuContainer ref={menuRef}>
          <MenuButton onClick={() => setMenuOpen(!menuOpen)} title="More options">
            <MoreVertical size={24} />
          </MenuButton>
          {menuOpen && (
            <DropdownMenu>
              {workoutSession?.file_storage_path && workoutSession?.file_bucket && (
                <MenuItem onClick={handleDownloadFitFile}>
                  <Download />
                  Download FIT File
                </MenuItem>
              )}
              <MenuItem $destructive onClick={handleDeleteClick}>
                <Trash2 />
                Delete Workout
              </MenuItem>
            </DropdownMenu>
          )}
        </MenuContainer>
      </Header>

      {/* Performance Overview */}
      <ContentGrid>
        <FullWidthSection>
          <WorkoutKeyMetrics
            activityType={workout.activity_type}
            workoutSession={workoutSession}
            avgStrokesPerLength={avgStrokesPerLength}
            avgPace={avgPace}
            idleTime={idleTime}
            avgSpeed={avgSpeed}
            calculatedMetrics={calculatedMetrics}
            metricTooltips={metricTooltips}
          />
        </FullWidthSection>
      </ContentGrid>

      {/* Workout Analysis Section */}
      {(analysisLoading ||
        (analysisResult && (analysisResult.totalWeights > 1 || analysisResult.insights.length > 0)) ||
        analysisError) && (
        <AnalysisSection>
          {analysisLoading && (
            <SkeletonWrapper>
              <Skeleton height={20} width="30%" style={{ marginBottom: 16 }} />
              <Skeleton height={16} count={3} style={{ marginBottom: 8 }} />
            </SkeletonWrapper>
          )}

          {analysisError && (
            <ErrorText>
              <AlertCircle size={16} />
              {analysisError.message}
            </ErrorText>
          )}

          {!analysisLoading && analysisResult && (
            <>
              <SectionTitle>
                <img src="/logo.svg" alt="Logo" width={24} height={24} />
                supacoach insights
              </SectionTitle>

              {analysisResult.totalWeights > 1 && (
                <ExecutionGrade $grade={analysisResult.grade}>
                  {analysisResult.grade === 'excellent' && <CheckCircle size={20} />}
                  {analysisResult.grade === 'partially' && <AlertCircle size={20} />}
                  {analysisResult.grade === 'poor' && <XCircle size={20} />}
                  {analysisResult.grade === 'not-graded' && <AlertCircle size={20} />}

                  <span>
                    {analysisResult.grade === 'excellent' && 'Excellent Execution'}
                    {analysisResult.grade === 'partially' && 'Partial Execution'}
                    {analysisResult.grade === 'poor' && 'Poor Execution'}
                    {analysisResult.grade === 'not-graded' && 'Not Graded'}
                    {analysisResult.totalWeights > 1 && ` (${Math.round(analysisResult.percentage * 100)}%)`}
                  </span>
                </ExecutionGrade>
              )}

              {analysisResult.insights.length > 0 && (
                <InsightsList>
                  {analysisResult.insights.map((insight, _index) => (
                    <InsightItem key={insight.description} $level={insight.level}>
                      {insight.category && (
                        <InsightCategory type="submit">
                          {insight.category.charAt(0).toUpperCase() + insight.category.slice(1)}
                        </InsightCategory>
                      )}
                      {insight.description}
                    </InsightItem>
                  ))}
                </InsightsList>
              )}
            </>
          )}
        </AnalysisSection>
      )}

      <ContentGrid2>
        {/* Laps Analysis */}
        {(laps.length > 0 || isLoading) && (
          <FullWidthSection>
            <Card id="intervals">
              <SectionTitle>Intervals</SectionTitle>
              <CardSubTitle>Breakdown of workout intervals</CardSubTitle>

              {isLoading && (
                <ChartSkeletonContainer>
                  <SkeletonWrapper style={{ width: '100%' }}>
                    <Skeleton height={300} />
                  </SkeletonWrapper>
                </ChartSkeletonContainer>
              )}

              {!isLoading && laps.length > 0 && steps && (
                <LapsChart
                  laps={laps}
                  workoutSteps={steps}
                  workoutId={workout.id}
                  isCycling={isCycling}
                  isSwimming={isSwimming}
                  isRunning={isRunning}
                />
              )}
            </Card>
          </FullWidthSection>
        )}

        {/* Splits Analysis */}
        {isRunning && (
          <FullWidthSection>
            <Card id="splits">
              <SectionTitle>Splits</SectionTitle>
              <CardSubTitle>Breakdown of metrics for each kilometer</CardSubTitle>
              <SplitsChart workoutId={workout.id} hrZones={workoutThresholds.hrZones} />
            </Card>
          </FullWidthSection>
        )}

        {/* Metrics Over Time */}
        <FullWidthSection>
          <Card id="metrics">
            <SectionTitle>Metrics</SectionTitle>
            <CardSubTitle>High resolution metrics data throughout the workouts</CardSubTitle>
            <MetricsChart
              workoutId={workout.id}
              vt1={workoutThresholds.vt1}
              vt2={workoutThresholds.vt2}
              maxHR={workoutThresholds.maxHr}
              activityType={workout.activity_type ?? undefined}
              fields={['heart_rate', 'power', 'speed', 'cadence', 'altitude']}
              defaultActiveMetrics={['heart_rate', 'power', 'altitude']}
            />
          </Card>
        </FullWidthSection>

        {/* Temperature Metrics */}
        {!isSwimming && (
          <FullWidthSection>
            <Card id="temperature">
              <SectionTitle>Temperature</SectionTitle>
              <CardSubTitle>All available temperature and heat strain measurements</CardSubTitle>
              <MetricsChart
                workoutId={workout.id}
                activityType={workout.activity_type ?? undefined}
                fields={['skin_temperature', 'heat_strain_index', 'temperature']}
                defaultActiveMetrics={['skin_temperature', 'heat_strain_index', 'temperature']}
              />
            </Card>
          </FullWidthSection>
        )}

        {/* Heart Rate Histogram */}
        {workoutThresholds.hrZones && (
          <FullWidthSection>
            <Card id="heart_rate_zones">
              <CardHeader>
                <CardHeaderContent>
                  <SectionTitle>Heart Rate Zones</SectionTitle>
                  <CardSubTitle>Distribution of time spent in each heart rate zone</CardSubTitle>
                </CardHeaderContent>
                {getBaseActivityType() && (
                  <CardHeaderActions>
                    <Button variant="secondary" size="sm" onClick={handleEditThresholds}>
                      <Edit2 size={14} />
                      Edit Thresholds
                    </Button>
                  </CardHeaderActions>
                )}
              </CardHeader>
              <HeartRateHistogram
                workoutId={workout.id}
                minHR={80}
                maxHR={workoutThresholds.maxHr}
                calculatedZones={workoutThresholds.hrZones}
                vt1={workoutThresholds.vt1}
                vt2={workoutThresholds.vt2}
                meanHR={hrStats?.average}
                stdDevHR={hrStats?.stdDev}
              />
            </Card>
          </FullWidthSection>
        )}

        {/* Power Zones */}
        {workoutThresholds?.powerZones && workoutThresholds.powerZones.length > 0 && powerStats && (
          <FullWidthSection>
            <Card id="power_zones">
              <SectionTitle>Power Zones</SectionTitle>
              <CardSubTitle>Distribution of time spent in each power zone</CardSubTitle>
              <PowerZonesChart workoutId={workout.id} powerZones={workoutThresholds.powerZones} />
            </Card>
          </FullWidthSection>
        )}

        {/* Critical Power (cycling/running) */}
        {analysisResult?.criticalPower && analysisResult.cpModel && analysisResult.criticalPower.length > 0 && (
          <FullWidthSection>
            <Card id="critical_power">
              <SectionTitle>Critical Power</SectionTitle>
              <CardSubTitle>
                Represents the highest sustained effort for each time period during this workout, compared to the
                previous 8 weeks ({analysisResult.cpReference?.dateRange[0]} to{' '}
                {analysisResult.cpReference?.dateRange[1]})
              </CardSubTitle>
              <CriticalPowerChart
                criticalPower={analysisResult.criticalPower}
                cpModel={analysisResult.cpModel}
                cpReference={analysisResult.cpReference}
              />
            </Card>
          </FullWidthSection>
        )}

        {/* W'bal (W prime balance) */}
        <FeatureFlag flag={FeatureFlagKey.WBalChart}>
          {analysisResult?.wBal && analysisResult.cpModel && analysisResult.wBal.length > 0 && (
            <FullWidthSection>
              <Card id="anaerobic_reserve">
                <SectionTitle>Anaerobic Reserve (W'bal)</SectionTitle>
                <CardSubTitle>Tracks anaerobic capacity depletion and recovery</CardSubTitle>
                <WBalChart
                  wBal={analysisResult.wBal}
                  wPrime={analysisResult.cpModel.wPrime}
                  cp={analysisResult.cpModel.cp}
                />
              </Card>
            </FullWidthSection>
          )}
        </FeatureFlag>
      </ContentGrid2>

      {/* Delete Confirmation Dialog */}
      {showDeleteConfirm && (
        <ConfirmDialog onClick={handleCancelDelete}>
          <ConfirmDialogContent onClick={(e) => e.stopPropagation()}>
            <ConfirmDialogTitle>Confirm Delete Workout</ConfirmDialogTitle>
            <ConfirmDialogMessage>
              Deleting the workout "{workout.title}" will permanently remove it from your account. This action cannot be
              undone.
            </ConfirmDialogMessage>
            <ConfirmDialogActions>
              <ConfirmButton $variant="primary" onClick={handleCancelDelete}>
                Cancel
              </ConfirmButton>
              <ConfirmButton $variant="danger" onClick={handleConfirmDelete}>
                Delete
              </ConfirmButton>
            </ConfirmDialogActions>
          </ConfirmDialogContent>
        </ConfirmDialog>
      )}
    </Container>
  )
}
